import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useAppDispatch, useAppSelector} from "@hooks/redux-hooks/hooks";
import {selectAzienda, selectPeriodo, selectSitePreferences, setupBusy} from "@commons/core.slice";
import PeriodoRif from "@components/PeriodoRif";
import Banner, {bannerStyle} from "@components/Banner";
import Loading from "@components/dataTable/Loading";
import {PeriodoForm, useHandlersPeriodoForm} from "@hooks/useHandlersPeriodoForm";
import {IndiceMese, Periodo, Trace} from "@commons/types";
import {FormProvider, useForm} from "react-hook-form";
import {AziendaRecord} from "@commons/mapping/AziendaRecord";
import {LavoratorePresenzeRecord} from "@commons/mapping/LavoratorePresenzeRecord";
import {SelectLavoratore} from "../comps/SelectLavoratore";
import {defaultOpt, PresenzeContext, usePresenze} from "@commons/hooks/usePresenze";
import {CalendarioGestionePLUS} from "./calendario.GestionePLUS";
import OpzioniPresenze from "../GestionePLUS/comps/OpzioniPresenze";
import {ModalOnSavePresenzeError, SavePresenzeError} from "../comps/ModalOnSavePresenzeError";
import {useStampaScorporo} from "@commons/services/presenze_api/stampa.queries";
import {toast} from "react-toastify";
import {customToastWithLink4Stampa} from "../../../features/export";
import {StampaFineMeseTask} from "@commons/services/presenze_calcolo/sub_calcolo/StampaFineMeseTask";

interface GPLUSForm extends PeriodoForm {
    idLavoratore: number;
}

export const GestionePlusPage: React.FC = () => {

    const dispatch = useAppDispatch();
    const selectedAz = useAppSelector(selectAzienda);
    const azienda: AziendaRecord | undefined = selectedAz ? selectedAz : undefined;// Azienda corrente
    const sitePreferences = useAppSelector(selectSitePreferences);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [disabledApplyPeriodo, setDisabledApplyPeriodo] = useState<boolean>(true);
    const [showLavoratori, setNextShowLavoratori] = useState<boolean>(true);
    const [showPresenze, setNextShowPresenze] = useState<boolean>(false);

    const [wasError, setWasError] = useState<SavePresenzeError | null>(null);
    const [isStampaInCorso, setStampaInCorso] = useState<boolean>(false);
    const stampa = useStampaScorporo();
    // Gestione selezione anno/mese
    const [periodo, setNextPeriodo] = useState<Periodo>(useAppSelector(selectPeriodo));

    const form = useForm<GPLUSForm>({defaultValues: {anno: periodo.anno, mese: periodo.mese, idLavoratore: 0}});
    const {getValues, setValue, handleSubmit,} = form;

    // Chiudo elenco giornaliero al variare dell'azienda
    useEffect(() => {
        //todo chiudiElenco...
    }, [azienda]);

    // Hook di caricamento e gestione Presenze
    const opt = useMemo(() => {
        return {
            ...defaultOpt,
            isPaginaPlus: true,
            caricaRichiesteWF: true,
            //selFirst: false,
            onLoadComplete: (lavoratori: LavoratorePresenzeRecord[], idLavoratore: number | null) => {
                console.log('Caricamento lavoratori completato!');
                dispatch(setupBusy(false));
                if (idLavoratore && lavoratori) {
                    const lavoratore = lavoratori.find(lav => lav.id === idLavoratore);
                    if (lavoratore?.loaded) {
                        setNextShowPresenze(true);
                    }
                }
            },
        }
    }, [dispatch]);

    const {
        getLavoratoreNext, getLavoratorePrev, reloadAll, lavoratore, selectLavoratore, lavoratori,
        isLoading: isLoadingPresenze, updLavoratore, setDayModified, updateDays, hasDaysToSave,
        context, ctrl, rollback, calcolaPeriodo, applicaGM,
    } = usePresenze(azienda, periodo, opt);
    // const isPeriodoChiuso = azienda && opt.user ? GestionePlus.statoPeriodo(azienda, periodo, lavoratore?.days || null, null, opt.user).chiuso:true;
    // console.log(lavoratore);

    // al cambiamento di anno o mese
    const onChangePeriodo = useCallback((_anno: number, _mese: number) => {
        setNextShowLavoratori(false);
        setNextShowPresenze(false);
        setDisabledApplyPeriodo(false);
    }, []);

    // alla conferma della selezione di anno o mese
    const onApplyPeriodo = useCallback((anno: number, mese: IndiceMese) => {
        setNextShowLavoratori(true);
        setNextPeriodo({anno, mese});
        setDisabledApplyPeriodo(true);
    }, []);

    const onReloadPeriodo = useCallback(() => reloadAll(), [reloadAll]);

    const periodoRifOptions = useHandlersPeriodoForm(form, onChangePeriodo, onApplyPeriodo, onReloadPeriodo, {autocommit: true});

    // A cambiamento del lavoratore
    const onChangeLavoratore = useCallback((_lavoratore: LavoratorePresenzeRecord) => {
        setNextShowPresenze(false);
    }, []);

    const selLavoratore = useCallback((id: number, ricarica?: boolean) => {
        setIsLoading(true);
        selectLavoratore(id, () => {
            setIsLoading(false);
            setNextShowPresenze(true);
        }, () => {
            setIsLoading(false);
            setNextShowPresenze(false);
        }, ricarica);
    }, [selectLavoratore]);

    // Alla conferma del lavoratore
    const onApplyLavoratore = useCallback((form: GPLUSForm) => {
        selLavoratore(form.idLavoratore);
    }, [selLavoratore]);

    const onReloadLavoratore = useCallback(() => {
        if (lavoratore) {
            selLavoratore(lavoratore.id, true);
        }
    }, [lavoratore, selLavoratore]);

    // Al lavoratore precedente o successivo (conferma automatica del lavoratore!)
    const changeLavoratore = (next: LavoratorePresenzeRecord | null) => {
        if (lavoratore && next) {
            if (next.id !== lavoratore.id) {
                setValue("idLavoratore", next.id);
                handleSubmit(onApplyLavoratore)();
            }
        }
    }

    // effetto per allineare la combobox al lavoratore selezionato da usePresenze
    useEffect(() => {
        if (getValues('idLavoratore') !== lavoratore?.id)
            setValue('idLavoratore', lavoratore?.id || 0);
    }, [getValues, setValue, lavoratore]);

    //const checkSviluppo = lavoratore && lavoratore.weeks !== null && lavoratore.weeks.length !== 0 && gestione.weeks!.length !== 0;
    const checkSviluppo = lavoratore && lavoratore.weeks !== null && lavoratore.weeks.length !== 0;

    //const {data: tabellaWF,} = useTabellaWF(selectedAz ? {idDominio: selectedAz.idDominio!, idTabella: selectedAz.idTabellaWF} : null); // Carico tabella impianto WF

    const salvaPresenze = async () => {
        try {
            const days = await ctrl?._salva(false,
                undefined,
                undefined,
                undefined,
                undefined,
                false,
                lavoratore!.days);
            if (days) {
                // aggiorno
                updateDays(days, (day2Update, dayModificato, _week, _lavoratore) => {
                    day2Update.modified = false
                    day2Update.lastUpdateTrace = Trace.factory(dayModificato?.lastUpdateTrace);
                })
            }
        } catch (err) {
            //wasError = true;
            console.log(err);
            setWasError({idDip: lavoratore!.id, message: err as string});
        }
    }

    const onReload = (idDip: number) => {
        if (ctrl) {
            ctrl.setLavoratoreById(idDip, true).then((lav) => {
                if (lav) {
                    updLavoratore(lav); // copio nel lavoratore le proprietà relative alle presenze appena caricate in <gestione>
                }
            });
        }
        setWasError(null);
    }

    const onRollback = (idDip: number) => {
        rollback(idDip);
        setWasError(null);
    }

    const stampaPeriodo = () => {
        //alert('Funzione "stampaPeriodo()" non implementata!');
        if (lavoratore) {
            setStampaInCorso(true);
            const opt = StampaFineMeseTask.factory({
                idAziendaMaster: azienda?.idAziendaMaster,
                idAzienda: azienda?.idAzienda,
                anno: periodo.anno,
                mese: periodo.mese,
                eseguiPer: 'SINGOLO',
                idDipSingolo: lavoratore.id,
                tipoStampa: 'SCORPORO',
            });
            stampa.mutateAsync(opt).then(({data}) => {
                const URI = data && data.linkSplf ? data.linkSplf : '???';
                toast.success(customToastWithLink4Stampa('scorporo', URI));
                setStampaInCorso(false);
            }, () => {
                toast.error("Errore in generazione della stampa!");
                setStampaInCorso(false);
            });
        }
    }

    return <>
        <Loading loading={isLoading || isLoadingPresenze}>
            <div className="mb-4">
                <FormProvider {...form} >
                    <form onSubmit={handleSubmit(onApplyLavoratore)}>
                        <PeriodoRif nameAnno="anno" nameComboMese="mese" className="w-2/3" {...periodoRifOptions}
                                    disabled={disabledApplyPeriodo}/>
                        {showLavoratori && <div className="pt-2">
                            <SelectLavoratore<GPLUSForm> lavoratori={lavoratori} name="idLavoratore" className="w-2/3"
                                                         hasApply={true}
                                                         onChange={onChangeLavoratore}
                                                         next={() => changeLavoratore(getLavoratoreNext())}
                                                         prev={() => changeLavoratore(getLavoratorePrev())}/></div>}
                    </form>
                </FormProvider>
            </div>

            {/* BANNERS DI SELEZIONE PERIODO E LAVORATORE */}
            {!showLavoratori && <Banner style={bannerStyle.warning} text={<>Seleziona un periodo e premi il bottone <strong>Applica</strong>!</>}/>}
            {!showPresenze && showLavoratori &&
                <Banner style={bannerStyle.warning} text={<>Seleziona un lavoratore e premi il bottone <strong>Applica</strong>!</>}/>}

            {/* CALENDARIO GESTIONE PLUS */}
            {(checkSviluppo && showPresenze) &&
                <PresenzeContext.Provider value={context}>
                    <CalendarioGestionePLUS lavoratore={lavoratore}
                                            next={() => changeLavoratore(getLavoratoreNext())}
                                            prev={() => changeLavoratore(getLavoratorePrev())}
                                            setDayModified={setDayModified} hasDaysToSave={hasDaysToSave}
                                            updLavoratore={updLavoratore}
                                            stampaPeriodo={stampaPeriodo}
                                            calcolaPeriodo={calcolaPeriodo}
                                            salvaPeriodo={salvaPresenze}
                                            ripristinaPeriodo={onReloadLavoratore}
                                            applicaGM={applicaGM}
                    />
                    <div className="bg-white">
                        <OpzioniPresenze sitePreferences={sitePreferences}/>
                    </div>
                </PresenzeContext.Provider>}

            {/* MESE NON SVILUPPATO */}
            {(lavoratore && !checkSviluppo && showPresenze) &&
                <Banner style={bannerStyle.danger}
                        text={
                            <>Per il lavoratore <span className="font-bold">{lavoratore.descrizione}</span> [#{lavoratore!.id}], non è presente lo <u>sviluppo del mese</u>.</>
                        }/>}

            {/* PROBLEMA!! */}
            {!(isLoading || isLoadingPresenze) && showPresenze && !lavoratore &&
                <Banner style={bannerStyle.danger} text={`C'e' un problema! / Non ci sono lavoratori per l'unita' locale selezionata!`}/>}

            <ModalOnSavePresenzeError wasError={wasError} onRollback={onRollback} onReload={onReload}></ModalOnSavePresenzeError>

        </Loading>
    </>;
}